import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const LANGUAGE_STORAGE_KEY = 'site_language';
const SUPPORTED_LANGUAGES = ['en', 'de', 'fr', 'es'];

const LOCALE_BY_LANGUAGE = {
  en: 'en-US',
  de: 'de-DE',
  fr: 'fr-FR',
  es: 'es-ES',
};

const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
];

const TRANSLATIONS = {
  en: {
    'lang.label': 'Language',
    'nav.home': 'Home',
    'nav.about': 'About',
    'nav.services': 'Services',
    'nav.bookAppointment': 'Book Appointment',
    'nav.contact': 'Contact',
    'action.bookSession': 'Book Session',
    'action.learnMore': 'Learn More',
    'footer.quickLinks': 'Quick Links',
    'footer.services': 'Services',
    'footer.contact': 'Contact',
    'footer.rightsReserved': 'All rights reserved.',
    'legal.privacyPolicy': 'Privacy Policy',
    'legal.termsOfService': 'Terms of Service',
    'legal.lastUpdated': 'Last updated:',
    'common.contactByPhone': 'Phone:',
    'common.contactByEmail': 'Email:',
    'common.location': 'Location:',
    'home.badge': 'Professional Wellness Services',
    'home.hero.title': 'Your Journey to Wellness Begins Here',
    'home.hero.subtitle': 'Professional Mental Health Coaching & Therapeutic Massage Services',
    'home.hero.description': "Supporting adults, couples, and teens through depression, PTSD, ADHD, and life's challenges with compassionate coaching and healing touch.",
    'home.hero.cta': 'Book Your Session',
    'home.expertise.title': 'Areas of Expertise',
    'home.expertise.subtitle': 'Specialized support for your unique mental health and wellness needs',
    'home.services.title': 'Services Offered',
    'home.services.subtitle': 'Comprehensive wellness solutions tailored to your needs',
    'home.experiences.title': 'Client Experiences',
    'home.experiences.subtitle': 'Hear from those who have embarked on their wellness journey',
    'home.cta.title': 'Ready to Begin Your Wellness Journey?',
    'home.cta.subtitle': 'Book your first session today and take the first step towards a healthier, more balanced life.',
    'home.cta.contactUs': 'Contact Us',
    'about.approachTitle': 'My Approach to Wellness',
    'about.languages': 'Languages',
    'about.serviceAreas': 'Service Areas',
    'about.qualifications': 'Qualifications & Expertise',
    'about.commitmentTitle': 'Commitment to Excellence',
    'about.coreValuesTitle': 'Core Values',
    'services.hero.title': 'Professional Wellness Services',
    'services.hero.subtitle': 'Comprehensive mental health coaching and therapeutic massage services designed to support your unique journey towards wellbeing',
    'services.badge.professionalService': 'Professional Service',
    'services.keyBenefits': 'Key Benefits:',
    'services.delivery': 'Delivery',
    'services.duration': 'Duration',
    'services.bookThisService': 'Book This Service',
    'services.comparison.title': 'Service Comparison',
    'services.comparison.feature': 'Feature',
    'services.comparison.coaching': 'Mental Health Coaching',
    'services.comparison.massage': 'Therapeutic Massage',
    'services.comparison.workshops': 'Workshops',
    'services.comparison.onlineAvailable': 'Online Available',
    'services.comparison.inPerson': 'In-Person (Munich)',
    'services.comparison.sessionLength': 'Session Length',
    'services.comparison.groupSessions': 'Group Sessions',
    'services.faqTitle': 'Frequently Asked Questions',
    'services.cta.title': 'Ready to Get Started?',
    'services.cta.subtitle': 'Take the first step towards better mental and physical wellbeing. Book your session today.',
    'services.cta.button': 'Book Your Session',
    'booking.toast.missingTitle': 'Missing Information',
    'booking.toast.missingDescription': 'Please fill in all required fields.',
    'booking.toast.successTitle': 'Appointment Request Received!',
    'booking.toast.successDescription': "Thank you for your request. I'll contact you shortly to confirm your appointment.",
    'booking.toast.errorTitle': 'Submission Error',
    'booking.toast.errorDescription': 'There was an error submitting your request. Please try again.',
    'booking.hero.title': 'Book Your Session',
    'booking.hero.subtitle': "Take the first step towards better wellbeing. Fill out the form below and I'll get back to you within 24 hours to confirm your appointment.",
    'booking.form.title': 'Appointment Request Form',
    'booking.form.description': 'All fields marked with * are required. Your information will be kept confidential.',
    'booking.form.successTitle': 'Request Submitted Successfully!',
    'booking.form.successDescription': "I'll be in touch soon to confirm your appointment details.",
    'booking.form.personalInfo': 'Personal Information',
    'booking.form.serviceDetails': 'Service Details',
    'booking.form.additionalInfo': 'Additional Information',
    'booking.form.fullName': 'Full Name *',
    'booking.form.email': 'Email Address *',
    'booking.form.phone': 'Phone Number *',
    'booking.form.serviceType': 'Service Type *',
    'booking.form.preferredDate': 'Preferred Date',
    'booking.form.preferredTime': 'Preferred Time',
    'booking.form.consultationType': 'Consultation Type',
    'booking.form.messageOptional': 'Message (Optional)',
    'booking.form.placeholder.fullName': 'Your full name',
    'booking.form.placeholder.email': 'your.email@example.com',
    'booking.form.placeholder.phone': '+49 XXX XXXXXXX',
    'booking.form.placeholder.serviceType': 'Select a service',
    'booking.form.placeholder.consultationType': 'Select consultation type',
    'booking.form.placeholder.message': "Please share any additional information or specific concerns you'd like to address...",
    'booking.form.consult.online': 'Online (Video Call)',
    'booking.form.consult.inPerson': 'In-Person (Munich)',
    'booking.form.consult.either': 'Either - Flexible',
    'booking.form.submitting': 'Submitting...',
    'booking.form.submit': 'Submit Appointment Request',
    'booking.form.privacy': 'By submitting this form, you agree to our privacy policy and terms of service.',
    'booking.nextSteps.title': 'What Happens Next?',
    'booking.nextSteps.step1Title': 'Submit Your Request',
    'booking.nextSteps.step1Description': 'Fill out and submit the appointment form',
    'booking.nextSteps.step2Title': 'Confirmation',
    'booking.nextSteps.step2Description': "I'll contact you within 24 hours to confirm",
    'booking.nextSteps.step3Title': 'Prepare',
    'booking.nextSteps.step3Description': 'Receive session details and preparation tips',
    'booking.nextSteps.step4Title': 'Your Session',
    'booking.nextSteps.step4Description': 'Meet for your wellness session',
    'booking.help.title': 'Need Help?',
    'booking.help.phone': 'Phone',
    'booking.help.email': 'Email',
    'contact.toast.missingTitle': 'Missing Information',
    'contact.toast.missingDescription': 'Please fill in all required fields.',
    'contact.toast.successTitle': 'Message Sent!',
    'contact.toast.successDescription': "Thank you for reaching out. I'll get back to you as soon as possible.",
    'contact.toast.errorTitle': 'Error',
    'contact.toast.errorDescription': 'Failed to send message. Please try again or contact directly via email.',
    'contact.hero.title': 'Get In Touch',
    'contact.hero.subtitle': "Have questions about my services or want to discuss your wellness journey? I'm here to help.",
    'contact.form.title': 'Send Me a Message',
    'contact.form.name': 'Name *',
    'contact.form.email': 'Email *',
    'contact.form.subject': 'Subject',
    'contact.form.message': 'Message *',
    'contact.form.placeholder.name': 'Your name',
    'contact.form.placeholder.email': 'your.email@example.com',
    'contact.form.placeholder.subject': 'What is this regarding?',
    'contact.form.placeholder.message': 'Your message...',
    'contact.form.sending': 'Sending...',
    'contact.form.send': 'Send Message',
    'contact.info.title': 'Contact Information',
    'contact.info.phone': 'Phone',
    'contact.info.email': 'Email',
    'contact.info.location': 'Location',
    'contact.response.title': 'Response Time',
    'contact.response.text': 'I typically respond to all inquiries within 24 hours during business days. For urgent matters, please call directly.',
    'contact.office.title': 'Office Hours',
    'contact.office.monFri': 'Monday - Friday:',
    'contact.office.monFriHours': '9:00 AM - 7:00 PM',
    'contact.office.sat': 'Saturday:',
    'contact.office.satHours': '10:00 AM - 4:00 PM',
    'contact.office.sun': 'Sunday:',
    'contact.office.sunHours': 'By Appointment',
    'contact.resources.title': 'Helpful Resources',
    'contact.resources.visit': 'Visit Website',
    'privacy.title': 'Privacy Policy',
    'privacy.introduction': 'Introduction',
    'privacy.contactSection': 'Contact Me',
    'terms.title': 'Terms of Service',
    'terms.agreement': 'Agreement to Terms',
    'terms.contactInfo': 'Contact Information',
    'appointmentType.coachingOnline': 'Mental Health Coaching (Online)',
    'appointmentType.coachingPerson': 'Mental Health Coaching (In-Person, Munich)',
    'appointmentType.massage': 'Therapeutic Massage (Mobile, Munich)',
    'appointmentType.workshop': 'Workshop/Training Inquiry',
    'appointmentType.consultation': 'Free Initial Consultation',
  },
  de: {
    'lang.label': 'Sprache',
    'nav.home': 'Startseite',
    'nav.about': 'Über mich',
    'nav.services': 'Leistungen',
    'nav.bookAppointment': 'Termin buchen',
    'nav.contact': 'Kontakt',
    'action.bookSession': 'Sitzung buchen',
    'action.learnMore': 'Mehr erfahren',
    'footer.quickLinks': 'Schnellzugriff',
    'footer.services': 'Leistungen',
    'footer.contact': 'Kontakt',
    'footer.rightsReserved': 'Alle Rechte vorbehalten.',
    'legal.privacyPolicy': 'Datenschutzerklärung',
    'legal.termsOfService': 'Nutzungsbedingungen',
    'legal.lastUpdated': 'Zuletzt aktualisiert:',
    'common.contactByPhone': 'Telefon:',
    'common.contactByEmail': 'E-Mail:',
    'common.location': 'Standort:',
    'home.badge': 'Professionelle Wellness-Dienstleistungen',
    'home.hero.title': 'Ihre Reise zu mehr Wohlbefinden beginnt hier',
    'home.hero.subtitle': 'Professionelles Mental-Health-Coaching & therapeutische Massage',
    'home.hero.description': 'Unterstützung für Erwachsene, Paare und Jugendliche bei Depressionen, PTBS, ADHS und Lebensherausforderungen mit empathischem Coaching und heilender Berührung.',
    'home.hero.cta': 'Sitzung buchen',
    'home.expertise.title': 'Schwerpunkte',
    'home.expertise.subtitle': 'Spezialisierte Unterstützung für Ihre individuellen Bedürfnisse',
    'home.services.title': 'Leistungsangebot',
    'home.services.subtitle': 'Ganzheitliche Lösungen für Ihr Wohlbefinden',
    'home.experiences.title': 'Erfahrungen von Klient:innen',
    'home.experiences.subtitle': 'Stimmen von Menschen auf ihrem Weg zum Wohlbefinden',
    'home.cta.title': 'Bereit für Ihren nächsten Schritt?',
    'home.cta.subtitle': 'Buchen Sie noch heute Ihre erste Sitzung und gehen Sie den ersten Schritt zu einem gesünderen, ausgeglicheneren Leben.',
    'home.cta.contactUs': 'Kontakt',
    'about.approachTitle': 'Mein Ansatz für Wohlbefinden',
    'about.languages': 'Sprachen',
    'about.serviceAreas': 'Einsatzgebiete',
    'about.qualifications': 'Qualifikationen & Expertise',
    'about.commitmentTitle': 'Engagement für Exzellenz',
    'about.coreValuesTitle': 'Kernwerte',
    'services.hero.title': 'Professionelle Wellness-Leistungen',
    'services.hero.subtitle': 'Umfassendes Mental-Health-Coaching und therapeutische Massage zur Unterstützung Ihres individuellen Weges.',
    'services.badge.professionalService': 'Professionelle Leistung',
    'services.keyBenefits': 'Wichtige Vorteile:',
    'services.delivery': 'Durchführung',
    'services.duration': 'Dauer',
    'services.bookThisService': 'Diese Leistung buchen',
    'services.comparison.title': 'Leistungsvergleich',
    'services.comparison.feature': 'Merkmal',
    'services.comparison.coaching': 'Mental-Health-Coaching',
    'services.comparison.massage': 'Therapeutische Massage',
    'services.comparison.workshops': 'Workshops',
    'services.comparison.onlineAvailable': 'Online verfügbar',
    'services.comparison.inPerson': 'Vor Ort (München)',
    'services.comparison.sessionLength': 'Sitzungsdauer',
    'services.comparison.groupSessions': 'Gruppensitzungen',
    'services.faqTitle': 'Häufige Fragen',
    'services.cta.title': 'Bereit loszulegen?',
    'services.cta.subtitle': 'Machen Sie den ersten Schritt zu besserem mentalem und körperlichem Wohlbefinden.',
    'services.cta.button': 'Sitzung buchen',
    'booking.toast.missingTitle': 'Fehlende Angaben',
    'booking.toast.missingDescription': 'Bitte füllen Sie alle Pflichtfelder aus.',
    'booking.toast.successTitle': 'Terminanfrage erhalten!',
    'booking.toast.successDescription': 'Vielen Dank für Ihre Anfrage. Ich melde mich zeitnah zur Bestätigung.',
    'booking.toast.errorTitle': 'Übermittlungsfehler',
    'booking.toast.errorDescription': 'Beim Absenden ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
    'booking.hero.title': 'Sitzung buchen',
    'booking.hero.subtitle': 'Machen Sie den ersten Schritt zu mehr Wohlbefinden. Ich melde mich innerhalb von 24 Stunden zur Terminbestätigung.',
    'booking.form.title': 'Terminanfrage',
    'booking.form.description': 'Alle mit * markierten Felder sind Pflichtfelder. Ihre Daten werden vertraulich behandelt.',
    'booking.form.successTitle': 'Anfrage erfolgreich gesendet!',
    'booking.form.successDescription': 'Ich melde mich bald zur Bestätigung Ihrer Termindetails.',
    'booking.form.personalInfo': 'Persönliche Angaben',
    'booking.form.serviceDetails': 'Leistungsdetails',
    'booking.form.additionalInfo': 'Zusätzliche Informationen',
    'booking.form.fullName': 'Vollständiger Name *',
    'booking.form.email': 'E-Mail-Adresse *',
    'booking.form.phone': 'Telefonnummer *',
    'booking.form.serviceType': 'Leistungsart *',
    'booking.form.preferredDate': 'Wunschtermin (Datum)',
    'booking.form.preferredTime': 'Wunschtermin (Uhrzeit)',
    'booking.form.consultationType': 'Art der Beratung',
    'booking.form.messageOptional': 'Nachricht (optional)',
    'booking.form.placeholder.fullName': 'Ihr vollständiger Name',
    'booking.form.placeholder.email': 'ihre.email@beispiel.de',
    'booking.form.placeholder.phone': '+49 XXX XXXXXXX',
    'booking.form.placeholder.serviceType': 'Leistung auswählen',
    'booking.form.placeholder.consultationType': 'Beratungsart auswählen',
    'booking.form.placeholder.message': 'Teilen Sie gerne zusätzliche Informationen oder Anliegen mit...',
    'booking.form.consult.online': 'Online (Videoanruf)',
    'booking.form.consult.inPerson': 'Vor Ort (München)',
    'booking.form.consult.either': 'Beides möglich',
    'booking.form.submitting': 'Wird gesendet...',
    'booking.form.submit': 'Terminanfrage senden',
    'booking.form.privacy': 'Mit dem Absenden stimmen Sie unserer Datenschutzerklärung und den Nutzungsbedingungen zu.',
    'booking.nextSteps.title': 'Wie geht es weiter?',
    'booking.nextSteps.step1Title': 'Anfrage absenden',
    'booking.nextSteps.step1Description': 'Formular ausfüllen und absenden',
    'booking.nextSteps.step2Title': 'Bestätigung',
    'booking.nextSteps.step2Description': 'Ich melde mich innerhalb von 24 Stunden',
    'booking.nextSteps.step3Title': 'Vorbereitung',
    'booking.nextSteps.step3Description': 'Sie erhalten Details und Vorbereitungstipps',
    'booking.nextSteps.step4Title': 'Ihre Sitzung',
    'booking.nextSteps.step4Description': 'Treffen zur Wellness-Sitzung',
    'booking.help.title': 'Brauchen Sie Hilfe?',
    'booking.help.phone': 'Telefon',
    'booking.help.email': 'E-Mail',
    'contact.toast.missingTitle': 'Fehlende Angaben',
    'contact.toast.missingDescription': 'Bitte füllen Sie alle Pflichtfelder aus.',
    'contact.toast.successTitle': 'Nachricht gesendet!',
    'contact.toast.successDescription': 'Vielen Dank für Ihre Nachricht. Ich melde mich so schnell wie möglich.',
    'contact.toast.errorTitle': 'Fehler',
    'contact.toast.errorDescription': 'Nachricht konnte nicht gesendet werden. Bitte erneut versuchen oder direkt per E-Mail kontaktieren.',
    'contact.hero.title': 'Kontakt aufnehmen',
    'contact.hero.subtitle': 'Fragen zu meinen Leistungen oder zu Ihrem Weg zu mehr Wohlbefinden? Ich helfe gerne.',
    'contact.form.title': 'Nachricht senden',
    'contact.form.name': 'Name *',
    'contact.form.email': 'E-Mail *',
    'contact.form.subject': 'Betreff',
    'contact.form.message': 'Nachricht *',
    'contact.form.placeholder.name': 'Ihr Name',
    'contact.form.placeholder.email': 'ihre.email@beispiel.de',
    'contact.form.placeholder.subject': 'Worum geht es?',
    'contact.form.placeholder.message': 'Ihre Nachricht...',
    'contact.form.sending': 'Wird gesendet...',
    'contact.form.send': 'Nachricht senden',
    'contact.info.title': 'Kontaktinformationen',
    'contact.info.phone': 'Telefon',
    'contact.info.email': 'E-Mail',
    'contact.info.location': 'Standort',
    'contact.response.title': 'Antwortzeit',
    'contact.response.text': 'Ich beantworte Anfragen in der Regel innerhalb von 24 Stunden an Werktagen. In dringenden Fällen bitte direkt anrufen.',
    'contact.office.title': 'Sprechzeiten',
    'contact.office.monFri': 'Montag - Freitag:',
    'contact.office.monFriHours': '09:00 - 19:00',
    'contact.office.sat': 'Samstag:',
    'contact.office.satHours': '10:00 - 16:00',
    'contact.office.sun': 'Sonntag:',
    'contact.office.sunHours': 'Nach Vereinbarung',
    'contact.resources.title': 'Hilfreiche Ressourcen',
    'contact.resources.visit': 'Website besuchen',
    'privacy.title': 'Datenschutzerklärung',
    'privacy.introduction': 'Einleitung',
    'privacy.contactSection': 'Kontakt',
    'terms.title': 'Nutzungsbedingungen',
    'terms.agreement': 'Zustimmung zu den Bedingungen',
    'terms.contactInfo': 'Kontaktinformationen',
    'appointmentType.coachingOnline': 'Mental-Health-Coaching (Online)',
    'appointmentType.coachingPerson': 'Mental-Health-Coaching (Vor Ort, München)',
    'appointmentType.massage': 'Therapeutische Massage (mobil, München)',
    'appointmentType.workshop': 'Workshop-/Training-Anfrage',
    'appointmentType.consultation': 'Kostenloses Erstgespräch',
  },
  fr: {
    'lang.label': 'Langue',
    'nav.home': 'Accueil',
    'nav.about': 'À propos',
    'nav.services': 'Services',
    'nav.bookAppointment': 'Prendre rendez-vous',
    'nav.contact': 'Contact',
    'action.bookSession': 'Réserver une séance',
    'action.learnMore': 'En savoir plus',
    'footer.quickLinks': 'Liens rapides',
    'footer.services': 'Services',
    'footer.contact': 'Contact',
    'footer.rightsReserved': 'Tous droits réservés.',
    'legal.privacyPolicy': 'Politique de confidentialité',
    'legal.termsOfService': "Conditions d'utilisation",
    'legal.lastUpdated': 'Dernière mise à jour :',
    'common.contactByPhone': 'Téléphone :',
    'common.contactByEmail': 'E-mail :',
    'common.location': 'Lieu :',
    'home.badge': 'Services de bien-être professionnels',
    'home.hero.title': 'Votre parcours vers le bien-être commence ici',
    'home.hero.subtitle': 'Coaching en santé mentale et massage thérapeutique professionnels',
    'home.hero.description': 'Accompagnement des adultes, couples et adolescents face à la dépression, au TSPT, au TDAH et aux défis de la vie.',
    'home.hero.cta': 'Réserver votre séance',
    'home.expertise.title': "Domaines d'expertise",
    'home.expertise.subtitle': 'Un accompagnement spécialisé adapté à vos besoins',
    'home.services.title': 'Services proposés',
    'home.services.subtitle': 'Des solutions de bien-être complètes et personnalisées',
    'home.experiences.title': 'Expériences clients',
    'home.experiences.subtitle': 'Découvrez les témoignages de celles et ceux qui ont commencé leur parcours',
    'home.cta.title': 'Prêt(e) à commencer votre parcours bien-être ?',
    'home.cta.subtitle': 'Réservez votre première séance dès aujourd’hui et faites le premier pas vers une vie plus équilibrée.',
    'home.cta.contactUs': 'Nous contacter',
    'about.approachTitle': 'Mon approche du bien-être',
    'about.languages': 'Langues',
    'about.serviceAreas': 'Zones de service',
    'about.qualifications': 'Qualifications et expertise',
    'about.commitmentTitle': "Engagement envers l'excellence",
    'about.coreValuesTitle': 'Valeurs fondamentales',
    'services.hero.title': 'Services de bien-être professionnels',
    'services.hero.subtitle': 'Coaching en santé mentale et massage thérapeutique pour soutenir votre parcours personnel.',
    'services.badge.professionalService': 'Service professionnel',
    'services.keyBenefits': 'Principaux bénéfices :',
    'services.delivery': 'Modalité',
    'services.duration': 'Durée',
    'services.bookThisService': 'Réserver ce service',
    'services.comparison.title': 'Comparaison des services',
    'services.comparison.feature': 'Caractéristique',
    'services.comparison.coaching': 'Coaching en santé mentale',
    'services.comparison.massage': 'Massage thérapeutique',
    'services.comparison.workshops': 'Ateliers',
    'services.comparison.onlineAvailable': 'Disponible en ligne',
    'services.comparison.inPerson': 'En présentiel (Munich)',
    'services.comparison.sessionLength': 'Durée de séance',
    'services.comparison.groupSessions': 'Séances de groupe',
    'services.faqTitle': 'Questions fréquentes',
    'services.cta.title': 'Prêt(e) à vous lancer ?',
    'services.cta.subtitle': 'Faites le premier pas vers un meilleur équilibre mental et physique.',
    'services.cta.button': 'Réserver votre séance',
    'booking.toast.missingTitle': 'Informations manquantes',
    'booking.toast.missingDescription': 'Veuillez remplir tous les champs obligatoires.',
    'booking.toast.successTitle': 'Demande reçue !',
    'booking.toast.successDescription': 'Merci pour votre demande. Je vous recontacterai rapidement pour confirmer.',
    'booking.toast.errorTitle': 'Erreur de soumission',
    'booking.toast.errorDescription': 'Une erreur est survenue lors de la soumission. Veuillez réessayer.',
    'booking.hero.title': 'Réserver votre séance',
    'booking.hero.subtitle': 'Faites le premier pas vers un meilleur bien-être. Je vous répondrai sous 24 heures pour confirmer.',
    'booking.form.title': 'Formulaire de demande de rendez-vous',
    'booking.form.description': 'Tous les champs marqués * sont obligatoires. Vos informations resteront confidentielles.',
    'booking.form.successTitle': 'Demande envoyée avec succès !',
    'booking.form.successDescription': 'Je vous contacterai bientôt pour confirmer les détails.',
    'booking.form.personalInfo': 'Informations personnelles',
    'booking.form.serviceDetails': 'Détails du service',
    'booking.form.additionalInfo': 'Informations complémentaires',
    'booking.form.fullName': 'Nom complet *',
    'booking.form.email': 'Adresse e-mail *',
    'booking.form.phone': 'Numéro de téléphone *',
    'booking.form.serviceType': 'Type de service *',
    'booking.form.preferredDate': 'Date souhaitée',
    'booking.form.preferredTime': 'Heure souhaitée',
    'booking.form.consultationType': 'Type de consultation',
    'booking.form.messageOptional': 'Message (optionnel)',
    'booking.form.placeholder.fullName': 'Votre nom complet',
    'booking.form.placeholder.email': 'votre.email@exemple.com',
    'booking.form.placeholder.phone': '+49 XXX XXXXXXX',
    'booking.form.placeholder.serviceType': 'Sélectionnez un service',
    'booking.form.placeholder.consultationType': 'Sélectionnez un type de consultation',
    'booking.form.placeholder.message': 'Partagez toute information complémentaire ou préoccupation spécifique...',
    'booking.form.consult.online': 'En ligne (visioconférence)',
    'booking.form.consult.inPerson': 'En présentiel (Munich)',
    'booking.form.consult.either': 'Les deux possibles',
    'booking.form.submitting': 'Envoi...',
    'booking.form.submit': 'Envoyer la demande',
    'booking.form.privacy': 'En envoyant ce formulaire, vous acceptez notre politique de confidentialité et nos conditions.',
    'booking.nextSteps.title': 'Et ensuite ?',
    'booking.nextSteps.step1Title': 'Envoyez votre demande',
    'booking.nextSteps.step1Description': 'Remplissez et envoyez le formulaire',
    'booking.nextSteps.step2Title': 'Confirmation',
    'booking.nextSteps.step2Description': 'Je vous contacte sous 24 heures',
    'booking.nextSteps.step3Title': 'Préparation',
    'booking.nextSteps.step3Description': 'Recevez les détails et conseils de préparation',
    'booking.nextSteps.step4Title': 'Votre séance',
    'booking.nextSteps.step4Description': 'Rencontre pour votre séance bien-être',
    'booking.help.title': "Besoin d'aide ?",
    'booking.help.phone': 'Téléphone',
    'booking.help.email': 'E-mail',
    'contact.toast.missingTitle': 'Informations manquantes',
    'contact.toast.missingDescription': 'Veuillez remplir tous les champs obligatoires.',
    'contact.toast.successTitle': 'Message envoyé !',
    'contact.toast.successDescription': 'Merci pour votre message. Je vous répondrai dès que possible.',
    'contact.toast.errorTitle': 'Erreur',
    'contact.toast.errorDescription': "Impossible d'envoyer le message. Veuillez réessayer ou contacter directement par e-mail.",
    'contact.hero.title': 'Entrer en contact',
    'contact.hero.subtitle': 'Vous avez des questions sur mes services ou votre parcours bien-être ? Je suis là pour vous aider.',
    'contact.form.title': "Envoyez-moi un message",
    'contact.form.name': 'Nom *',
    'contact.form.email': 'E-mail *',
    'contact.form.subject': 'Objet',
    'contact.form.message': 'Message *',
    'contact.form.placeholder.name': 'Votre nom',
    'contact.form.placeholder.email': 'votre.email@exemple.com',
    'contact.form.placeholder.subject': "À quel sujet ?",
    'contact.form.placeholder.message': 'Votre message...',
    'contact.form.sending': 'Envoi...',
    'contact.form.send': 'Envoyer le message',
    'contact.info.title': 'Coordonnées',
    'contact.info.phone': 'Téléphone',
    'contact.info.email': 'E-mail',
    'contact.info.location': 'Lieu',
    'contact.response.title': 'Temps de réponse',
    'contact.response.text': 'Je réponds généralement sous 24 heures les jours ouvrables. En cas d’urgence, merci d’appeler directement.',
    'contact.office.title': 'Horaires',
    'contact.office.monFri': 'Lundi - Vendredi :',
    'contact.office.monFriHours': '9h00 - 19h00',
    'contact.office.sat': 'Samedi :',
    'contact.office.satHours': '10h00 - 16h00',
    'contact.office.sun': 'Dimanche :',
    'contact.office.sunHours': 'Sur rendez-vous',
    'contact.resources.title': 'Ressources utiles',
    'contact.resources.visit': 'Visiter le site',
    'privacy.title': 'Politique de confidentialité',
    'privacy.introduction': 'Introduction',
    'privacy.contactSection': 'Me contacter',
    'terms.title': "Conditions d'utilisation",
    'terms.agreement': 'Acceptation des conditions',
    'terms.contactInfo': 'Informations de contact',
    'appointmentType.coachingOnline': 'Coaching santé mentale (en ligne)',
    'appointmentType.coachingPerson': 'Coaching santé mentale (en présentiel, Munich)',
    'appointmentType.massage': 'Massage thérapeutique (mobile, Munich)',
    'appointmentType.workshop': 'Demande atelier/formation',
    'appointmentType.consultation': 'Consultation initiale gratuite',
  },
  es: {
    'lang.label': 'Idioma',
    'nav.home': 'Inicio',
    'nav.about': 'Sobre mí',
    'nav.services': 'Servicios',
    'nav.bookAppointment': 'Reservar cita',
    'nav.contact': 'Contacto',
    'action.bookSession': 'Reservar sesión',
    'action.learnMore': 'Más información',
    'footer.quickLinks': 'Enlaces rápidos',
    'footer.services': 'Servicios',
    'footer.contact': 'Contacto',
    'footer.rightsReserved': 'Todos los derechos reservados.',
    'legal.privacyPolicy': 'Política de privacidad',
    'legal.termsOfService': 'Términos del servicio',
    'legal.lastUpdated': 'Última actualización:',
    'common.contactByPhone': 'Teléfono:',
    'common.contactByEmail': 'Correo electrónico:',
    'common.location': 'Ubicación:',
    'home.badge': 'Servicios profesionales de bienestar',
    'home.hero.title': 'Tu camino hacia el bienestar empieza aquí',
    'home.hero.subtitle': 'Coaching de salud mental y masaje terapéutico profesional',
    'home.hero.description': 'Apoyo para adultos, parejas y adolescentes ante depresión, TEPT, TDAH y desafíos de la vida.',
    'home.hero.cta': 'Reservar tu sesión',
    'home.expertise.title': 'Áreas de especialización',
    'home.expertise.subtitle': 'Apoyo especializado para tus necesidades únicas',
    'home.services.title': 'Servicios ofrecidos',
    'home.services.subtitle': 'Soluciones integrales de bienestar adaptadas a ti',
    'home.experiences.title': 'Experiencias de clientes',
    'home.experiences.subtitle': 'Conoce testimonios de quienes iniciaron su proceso',
    'home.cta.title': '¿Listo/a para comenzar tu camino de bienestar?',
    'home.cta.subtitle': 'Reserva hoy tu primera sesión y da el primer paso hacia una vida más equilibrada.',
    'home.cta.contactUs': 'Contáctanos',
    'about.approachTitle': 'Mi enfoque del bienestar',
    'about.languages': 'Idiomas',
    'about.serviceAreas': 'Áreas de servicio',
    'about.qualifications': 'Cualificaciones y experiencia',
    'about.commitmentTitle': 'Compromiso con la excelencia',
    'about.coreValuesTitle': 'Valores fundamentales',
    'services.hero.title': 'Servicios profesionales de bienestar',
    'services.hero.subtitle': 'Coaching de salud mental y masaje terapéutico para apoyar tu proceso personal.',
    'services.badge.professionalService': 'Servicio profesional',
    'services.keyBenefits': 'Beneficios clave:',
    'services.delivery': 'Modalidad',
    'services.duration': 'Duración',
    'services.bookThisService': 'Reservar este servicio',
    'services.comparison.title': 'Comparación de servicios',
    'services.comparison.feature': 'Características',
    'services.comparison.coaching': 'Coaching de salud mental',
    'services.comparison.massage': 'Masaje terapéutico',
    'services.comparison.workshops': 'Talleres',
    'services.comparison.onlineAvailable': 'Disponible en línea',
    'services.comparison.inPerson': 'Presencial (Múnich)',
    'services.comparison.sessionLength': 'Duración de la sesión',
    'services.comparison.groupSessions': 'Sesiones grupales',
    'services.faqTitle': 'Preguntas frecuentes',
    'services.cta.title': '¿Listo/a para empezar?',
    'services.cta.subtitle': 'Da el primer paso hacia un mejor bienestar mental y físico.',
    'services.cta.button': 'Reservar tu sesión',
    'booking.toast.missingTitle': 'Falta información',
    'booking.toast.missingDescription': 'Completa todos los campos obligatorios.',
    'booking.toast.successTitle': '¡Solicitud recibida!',
    'booking.toast.successDescription': 'Gracias por tu solicitud. Te contactaré pronto para confirmar.',
    'booking.toast.errorTitle': 'Error de envío',
    'booking.toast.errorDescription': 'Hubo un error al enviar tu solicitud. Inténtalo de nuevo.',
    'booking.hero.title': 'Reserva tu sesión',
    'booking.hero.subtitle': 'Da el primer paso hacia un mejor bienestar. Te responderé en 24 horas para confirmar.',
    'booking.form.title': 'Formulario de solicitud de cita',
    'booking.form.description': 'Todos los campos marcados con * son obligatorios. Tu información será confidencial.',
    'booking.form.successTitle': '¡Solicitud enviada con éxito!',
    'booking.form.successDescription': 'Pronto te contactaré para confirmar los detalles de tu cita.',
    'booking.form.personalInfo': 'Información personal',
    'booking.form.serviceDetails': 'Detalles del servicio',
    'booking.form.additionalInfo': 'Información adicional',
    'booking.form.fullName': 'Nombre completo *',
    'booking.form.email': 'Correo electrónico *',
    'booking.form.phone': 'Teléfono *',
    'booking.form.serviceType': 'Tipo de servicio *',
    'booking.form.preferredDate': 'Fecha preferida',
    'booking.form.preferredTime': 'Hora preferida',
    'booking.form.consultationType': 'Tipo de consulta',
    'booking.form.messageOptional': 'Mensaje (opcional)',
    'booking.form.placeholder.fullName': 'Tu nombre completo',
    'booking.form.placeholder.email': 'tu.correo@ejemplo.com',
    'booking.form.placeholder.phone': '+49 XXX XXXXXXX',
    'booking.form.placeholder.serviceType': 'Selecciona un servicio',
    'booking.form.placeholder.consultationType': 'Selecciona tipo de consulta',
    'booking.form.placeholder.message': 'Comparte información adicional o inquietudes específicas...',
    'booking.form.consult.online': 'En línea (videollamada)',
    'booking.form.consult.inPerson': 'Presencial (Múnich)',
    'booking.form.consult.either': 'Flexible - cualquiera',
    'booking.form.submitting': 'Enviando...',
    'booking.form.submit': 'Enviar solicitud de cita',
    'booking.form.privacy': 'Al enviar este formulario, aceptas nuestra política de privacidad y términos del servicio.',
    'booking.nextSteps.title': '¿Qué sucede después?',
    'booking.nextSteps.step1Title': 'Envía tu solicitud',
    'booking.nextSteps.step1Description': 'Completa y envía el formulario',
    'booking.nextSteps.step2Title': 'Confirmación',
    'booking.nextSteps.step2Description': 'Te contactaré dentro de 24 horas para confirmar',
    'booking.nextSteps.step3Title': 'Preparación',
    'booking.nextSteps.step3Description': 'Recibe detalles y recomendaciones previas',
    'booking.nextSteps.step4Title': 'Tu sesión',
    'booking.nextSteps.step4Description': 'Nos reunimos para tu sesión de bienestar',
    'booking.help.title': '¿Necesitas ayuda?',
    'booking.help.phone': 'Teléfono',
    'booking.help.email': 'Correo electrónico',
    'contact.toast.missingTitle': 'Falta información',
    'contact.toast.missingDescription': 'Completa todos los campos obligatorios.',
    'contact.toast.successTitle': '¡Mensaje enviado!',
    'contact.toast.successDescription': 'Gracias por escribir. Te responderé lo antes posible.',
    'contact.toast.errorTitle': 'Error',
    'contact.toast.errorDescription': 'No se pudo enviar el mensaje. Inténtalo de nuevo o contacta por correo.',
    'contact.hero.title': 'Ponte en contacto',
    'contact.hero.subtitle': '¿Tienes preguntas sobre mis servicios o tu proceso de bienestar? Estoy aquí para ayudarte.',
    'contact.form.title': 'Envíame un mensaje',
    'contact.form.name': 'Nombre *',
    'contact.form.email': 'Correo electrónico *',
    'contact.form.subject': 'Asunto',
    'contact.form.message': 'Mensaje *',
    'contact.form.placeholder.name': 'Tu nombre',
    'contact.form.placeholder.email': 'tu.correo@ejemplo.com',
    'contact.form.placeholder.subject': '¿Sobre qué es?',
    'contact.form.placeholder.message': 'Tu mensaje...',
    'contact.form.sending': 'Enviando...',
    'contact.form.send': 'Enviar mensaje',
    'contact.info.title': 'Información de contacto',
    'contact.info.phone': 'Teléfono',
    'contact.info.email': 'Correo electrónico',
    'contact.info.location': 'Ubicación',
    'contact.response.title': 'Tiempo de respuesta',
    'contact.response.text': 'Normalmente respondo en 24 horas en días laborables. Para asuntos urgentes, llama directamente.',
    'contact.office.title': 'Horario de atención',
    'contact.office.monFri': 'Lunes - Viernes:',
    'contact.office.monFriHours': '9:00 - 19:00',
    'contact.office.sat': 'Sábado:',
    'contact.office.satHours': '10:00 - 16:00',
    'contact.office.sun': 'Domingo:',
    'contact.office.sunHours': 'Con cita previa',
    'contact.resources.title': 'Recursos útiles',
    'contact.resources.visit': 'Visitar sitio web',
    'privacy.title': 'Política de privacidad',
    'privacy.introduction': 'Introducción',
    'privacy.contactSection': 'Contáctame',
    'terms.title': 'Términos del servicio',
    'terms.agreement': 'Aceptación de los términos',
    'terms.contactInfo': 'Información de contacto',
    'appointmentType.coachingOnline': 'Coaching de salud mental (en línea)',
    'appointmentType.coachingPerson': 'Coaching de salud mental (presencial, Múnich)',
    'appointmentType.massage': 'Masaje terapéutico (móvil, Múnich)',
    'appointmentType.workshop': 'Consulta de taller/formación',
    'appointmentType.consultation': 'Consulta inicial gratuita',
  },
};

const LanguageContext = createContext(null);

const normalizeLanguage = (language) => {
  if (!language || typeof language !== 'string') {
    return 'en';
  }

  const cleaned = language.toLowerCase().trim();
  if (SUPPORTED_LANGUAGES.includes(cleaned)) {
    return cleaned;
  }

  if (cleaned.startsWith('de')) {
    return 'de';
  }
  if (cleaned.startsWith('fr')) {
    return 'fr';
  }
  if (cleaned.startsWith('es')) {
    return 'es';
  }
  return 'en';
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const stored = typeof window !== 'undefined'
      ? window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
      : null;

    if (stored) {
      return normalizeLanguage(stored);
    }

    const browserLanguage = typeof navigator !== 'undefined' ? navigator.language : 'en';
    return normalizeLanguage(browserLanguage);
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    }

    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
  }, [language]);

  const tr = useCallback(
    (key, fallback = '') => {
      const translated = TRANSLATIONS[language]?.[key];
      if (typeof translated === 'string') {
        return translated;
      }

      const defaultTranslation = TRANSLATIONS.en?.[key];
      if (typeof defaultTranslation === 'string') {
        return defaultTranslation;
      }

      return fallback || key;
    },
    [language],
  );

  const localizeText = useCallback(
    (value) => {
      if (value === null || value === undefined) {
        return '';
      }

      if (typeof value === 'string' || typeof value === 'number') {
        return String(value);
      }

      if (typeof value === 'object' && !Array.isArray(value)) {
        const maybe = value[language] ?? value.en ?? value.de ?? value.fr ?? value.es;
        if (typeof maybe === 'string' || typeof maybe === 'number') {
          return String(maybe);
        }
      }

      return String(value);
    },
    [language],
  );

  const formatDate = useCallback(
    (value) => {
      const dateValue = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(dateValue.getTime())) {
        return '';
      }

      try {
        return new Intl.DateTimeFormat(LOCALE_BY_LANGUAGE[language] || LOCALE_BY_LANGUAGE.en).format(dateValue);
      } catch (error) {
        return dateValue.toLocaleDateString();
      }
    },
    [language],
  );

  const contextValue = useMemo(
    () => ({
      language,
      setLanguage,
      languageOptions: LANGUAGE_OPTIONS,
      tr,
      localizeText,
      formatDate,
    }),
    [language, tr, localizeText, formatDate],
  );

  return <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }

  return context;
};
